import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  InformationalScreen,
  LoadingModal,
  useDirection,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { FormContainer, MainContainer, PhoneInput } from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { useApiError, useAuth } from 'Hooks';
import { updatePhone } from 'Services/patient';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

const mapStateToProps = ({
  auth: { account_id },
  env: { osType, osVersion, app, appVersion },
}: RootState) => ({
  accountId: account_id,
  osType,
  osVersion,
  app,
  appVersion,
});

const initialValuesFormik = {
  phone: '',
  repeatPhone: '',
};

type UpdatePhoneFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const UpdatePhone = ({ accountId, osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z
    .object({
      phone: z
        .string({
          error: i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.errors.required'),
        })
        .trim()
        .min(1, {
          error: i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.errors.required'),
        })
        .refine((value) => parsePhoneNumberFromString(value)?.isPossible() ?? false, {
          error: i18n.t(
            'UpdatePhone.content.updatePhoneForm.formField.phone.errors.phoneNumberValid'
          ),
        }),
      repeatPhone: z
        .string({
          error: i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.errors.required'),
        })
        .trim()
        .min(1, {
          error: i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.errors.required'),
        }),
    })
    .refine((data) => data.phone === data.repeatPhone, {
      path: ['repeatPhone'],
      error: i18n.t(
        'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.errors.phoneMustMatch'
      ),
    });

  const handleClickBack = useCallback(() => {
    mediator.publish('router:navigate', '/account-information');
  }, []);

  const isLtr = useDirection() === 'ltr';

  const { showApiErrorModal, showNetworkErrorModal, showCustomError } = useApiError();

  const { handleLoginError, showHcpError } = useAuth('changepassword');

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: (name: string, value: unknown) => void,
      setTouched: (fields: Record<string, boolean>) => void
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            setFieldValue('phone', '');
            setFieldValue('repeatPhone', '');
          });
          break;

        case ApiStatus.BAD_REQUEST: {
          setFieldValue('phone', '');
          setFieldValue('repeatPhone', '');
          setTouched({});
          resetForm();

          const errorCode = error.response?.data.code;

          if (errorCode === ApiStatus.PHONE_CODE_INVALID) {
            showCustomError(i18n.t('Global.modals.invalidPhoneCode.body'));
          } else {
            showCustomError(error.response?.data?.details || '');
          }

          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [handleLoginError, showApiErrorModal, showHcpError, showCustomError, showNetworkErrorModal]
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdatePhoneFormValues,
      { setSubmitting, resetForm, setFieldValue, setTouched }: FormikHelpers<UpdatePhoneFormValues>
    ) => {
      try {
        await updatePhone(
          {
            userID: accountId,
            phone: values.phone,
          },
          osType,
          osVersion,
          app,
          appVersion
        );

        notifyApp('adc-webview:update-phone');
        mediator.publish('router:navigate', '/account-information');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched
        );
      }

      setSubmitting(false);
    },
    [accountId, app, appVersion, osType, osVersion, handleApiRequestError]
  );

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
    dirty,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validateOnBlur: true,
    validationSchema: toFormikValidationSchema(validationSchema),
  });

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen>
        <InformationalScreen.Page
          topNavigationProps={{
            title: i18n.t('UpdatePhone.title'),
            buttonStart: {
              element: isLtr ? (
                <IconArrowLeft testID="UpdatePhone.back-button" />
              ) : (
                <IconArrowRight testID="UpdatePhone.back-button" />
              ),
              label: i18n.t('UpdatePhone.title'),
              onPress: handleClickBack,
            },
            testID: 'UpdatePhone.title',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              <PhoneInput
                aria-label={i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.label')}
                placeholder={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.phone.placeholder'
                )}
                label={i18n.t('UpdatePhone.content.updatePhoneForm.formField.phone.label')}
                errorMessage={errors.phone}
                isInvalid={Boolean(errors.phone && touched.phone)}
                testID="UpdatePhone.phone"
                value={values.phone}
                onChangeText={handleChange('phone')}
                onBlur={handleBlur('phone')}
              />
              <PhoneInput
                aria-label={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.label'
                )}
                placeholder={i18n.t(
                  'UpdatePhone.content.updatePhoneForm.formField.repeatPhone.placeholder'
                )}
                label={i18n.t('UpdatePhone.content.updatePhoneForm.formField.repeatPhone.label')}
                errorMessage={errors.repeatPhone}
                isInvalid={Boolean(errors.repeatPhone && touched.repeatPhone)}
                testID="UpdatePhone.repeatPhone"
                value={values.repeatPhone}
                onChangeText={handleChange('repeatPhone')}
                onBlur={handleBlur('repeatPhone')}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={!isValid || isSubmitting || !dirty}
              onPress={() => {
                handleSubmit();
              }}
              testID="UpdatePhone.save-button"
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdatePhone);
